import Phaser from "phaser";
import type { WindowManager } from "../window-manager";

export interface BaseWindowConfig {
  title: string;
  side: "left" | "right" | "center";
}

export abstract class BaseWindow {
  protected scene: Phaser.Scene;
  protected container: Phaser.GameObjects.Container | null = null;
  protected visible: boolean = false;

  // When set, window containers are added to this panel instead of uiLayer (used by hub carousel)
  protected screenContainer: Phaser.GameObjects.Container | null = null;

  // UI layer — window containers are added here when screenContainer is not set
  protected uiLayer: Phaser.GameObjects.Container | null = null;

  // UI elements
  protected bg: Phaser.GameObjects.Graphics | null = null;
  protected titleText: Phaser.GameObjects.Text | null = null;

  // Window configuration
  protected config: BaseWindowConfig | null = null;

  // Standard window dimensions
  private readonly CORNER_RADIUS = 12;
  private readonly BG_ALPHA = 0.92;

  // Standard content padding
  protected readonly CONTENT_PADDING_X = 16;
  protected readonly CONTENT_PADDING_Y = 40;
  protected readonly HEADER_HEIGHT = 46;

  protected x: number = 0;
  protected y: number = 0;
  protected width: number = 0;
  protected height: number = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  public setScreenContainer(container: Phaser.GameObjects.Container | null): void {
    this.screenContainer = container;
  }

  public setUiLayer(layer: Phaser.GameObjects.Container | null): void {
    this.uiLayer = layer;
  }

  protected addContainerToParent(container: Phaser.GameObjects.Container): void {
    if (this.screenContainer) {
      this.screenContainer.add(container);
    } else if (this.uiLayer) {
      this.uiLayer.add(container);
    }
  }

  /**
   * Optional per-window layout overrides.
   * Subclasses can override to customize window frame size/position.
   */
  protected getLayoutOverrides(
    _config: BaseWindowConfig,
  ): Partial<{ x: number; y: number; width: number; height: number }> {
    return {};
  }

  /**
   * Creates the base window elements (background, title, close button)
   * This should be called by subclasses in their show() method
   */
  protected createBaseWindow(
    config: BaseWindowConfig,
  ): Phaser.GameObjects.GameObject[] {
    this.config = config;
    const { title, side } = config;

    // Calculate standard window dimensions and position
    const gameWidth = this.scene.scale.width;
    const gameHeight = this.scene.scale.height;
    const marginsX = 48;
    const marginsY = 32;

    // If center, use full width; otherwise use half width
    this.width =
      side === "center"
        ? gameWidth - marginsX * 2
        : gameWidth / 2 - marginsX - marginsX / 2;
    // In BaseScene (hub), leave room for the bottom nav bar (80px + 8px buffer)
    const navPadding = this.scene.scene.key === "BaseScene" ? 88 : 0;
    this.height = gameHeight - 80 - marginsY - navPadding;
    this.y = 80;

    // Calculate x position based on side
    if (side === "center") {
      this.x = marginsX;
    } else if (side === "right") {
      this.x = gameWidth / 2 + marginsX / 2;
    } else {
      // left
      this.x = marginsX;
    }

    const overrides = this.getLayoutOverrides(config);
    if (overrides.x !== undefined) this.x = overrides.x;
    if (overrides.y !== undefined) this.y = overrides.y;
    if (overrides.width !== undefined) this.width = overrides.width;
    if (overrides.height !== undefined) this.height = overrides.height;

    const elements: Phaser.GameObjects.GameObject[] = [];

    // ── Deep shadow layer ────────────────────────────────────────────────────
    const shadowGraphics = this.scene.add.graphics();
    shadowGraphics.fillStyle(0x000000, 0.45);
    shadowGraphics.fillRoundedRect(
      this.x + 4,
      this.y + 6,
      this.width,
      this.height,
      this.CORNER_RADIUS,
    );
    shadowGraphics.setScrollFactor(0);
    elements.push(shadowGraphics);

    // ── Outer border / glow (navy) ───────────────────────────────────────────
    const borderGraphics = this.scene.add.graphics();
    const borderWidth = 3;
    borderGraphics.lineStyle(borderWidth, 0x0d1e2e, 1);
    borderGraphics.strokeRoundedRect(
      this.x - borderWidth / 2,
      this.y - borderWidth / 2,
      this.width + borderWidth,
      this.height + borderWidth,
      this.CORNER_RADIUS,
    );
    borderGraphics.setScrollFactor(0);
    elements.push(borderGraphics);

    // ── Inner accent border (subtle cyan) ────────────────────────────────────
    const accentBorder = this.scene.add.graphics();
    accentBorder.lineStyle(1, 0x5dd9e8, 0.3);
    accentBorder.strokeRoundedRect(
      this.x + 2,
      this.y + 2,
      this.width - 4,
      this.height - 4,
      this.CORNER_RADIUS - 2,
    );
    accentBorder.setScrollFactor(0);
    elements.push(accentBorder);

    // ── Main background (dark blue-grey) ─────────────────────────────────────
    // Slightly smaller radius than the border (same reduction as the accent border
    // below) so the fill's corner curve stays inside the border's, instead of
    // leaving a sliver of the background visible between fill and border.
    const fillCornerRadius = this.CORNER_RADIUS - 2;
    this.bg = this.scene.add.graphics();
    this.bg.fillStyle(0x0f1e2d, this.BG_ALPHA);
    this.bg.fillRoundedRect(
      this.x,
      this.y,
      this.width,
      this.height,
      fillCornerRadius,
    );
    this.bg.setScrollFactor(0);
    elements.push(this.bg);

    // ── Header background ─────────────────────────────────────────────────────
    const headerBg = this.scene.add.graphics();
    // Gradient-like two-tone: slightly lighter strip at top
    headerBg.fillStyle(0x1a3550, 1);
    headerBg.fillRoundedRect(this.x, this.y, this.width, this.HEADER_HEIGHT, {
      tl: fillCornerRadius,
      tr: fillCornerRadius,
      bl: 0,
      br: 0,
    });
    headerBg.setScrollFactor(0);
    elements.push(headerBg);

    // Cyan bottom rule under header
    const headerRule = this.scene.add.graphics();
    headerRule.lineStyle(2, 0x5dd9e8, 0.7);
    headerRule.lineBetween(
      this.x + 2,
      this.y + this.HEADER_HEIGHT,
      this.x + this.width - 2,
      this.y + this.HEADER_HEIGHT,
    );
    headerRule.setScrollFactor(0);
    elements.push(headerRule);

    // ── Title ────────────────────────────────────────────────────────────────
    this.titleText = this.scene.add
      .text(
        this.x + this.CONTENT_PADDING_X,
        this.y + this.HEADER_HEIGHT / 2 - 1,
        title.toUpperCase(),
        {
          fontSize: "22px",
          color: "#5dd9e8",
          fontFamily: "Anton",
          align: "left",
          stroke: "#0d1e2e",
          strokeThickness: 3,
        },
      )
      .setOrigin(0, 0.5);
    this.titleText.setScrollFactor(0);
    elements.push(this.titleText);

    return elements;
  }

  /**
   * Updates the title text
   */
  protected setTitle(title: string): void {
    if (this.titleText) {
      this.titleText.setText(title);
    }
  }

  /**
   * Abstract method that subclasses must implement to show the window
   * Subclasses can override with different parameters as needed
   */
  abstract show(...args: any[]): void;

  /**
   * Hides the window and cleans up resources
   */
  hide(): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    this.bg = null;
    this.titleText = null;
    this.config = null;
    this.visible = false;   
  }

  /**
   * Returns whether the window is currently visible
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Destroys the window and all its elements
   */
  destroy(): void {
    this.hide();
  }

  /**
   * Helper method to get window bounds for positioning child elements
   */
  protected getWindowBounds(): {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null {
    return { x: this.x, y: this.y, width: this.width, height: this.height };
  }
}
